import { useState, useEffect } from "react";
import { Typography, Grid, TextField, Button, FormControl, InputLabel, Select, MenuItem, Box } from "@mui/material";
import { Fragment } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createProduct } from "../../../Redux/Customers/Product/Action";
import { useNavigate } from "react-router-dom";
import { findCategories } from "../../../Redux/Customers/Categories/Action";

const defaultSizes = [
  { size: "S", quantity: 0 },
  { size: "M", quantity: 0 },
  { size: "L", quantity: 0 },
];

const CreateProductForm = () => {
  const [productData, setProductData] = useState({
    imageUrl: "",
    brand: "",
    title: "",
    colors: [
      { name: "", sizes: defaultSizes.map(size => ({ ...size })) }
    ],
    discountedPrice: "",
    price: "",
    discountPersent: "",
    category: "",
    description: "",
  });

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { categories, loading, error } = useSelector((state) => state);

  useEffect(() => {
    dispatch(findCategories());
  }, [dispatch]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedData = { ...productData, [name]: value };

    // Calculate discounted price if price or discount percentage changes
    if (name === "price" || name === "discountPersent") {
      const price = parseFloat(updatedData.price) || 0;
      const discountPersent = parseFloat(updatedData.discountPersent) || 0;
      updatedData.discountedPrice = (price - (price * discountPersent) / 100).toFixed(2);
    }

    setProductData(updatedData);
  };

  const handleColorChange = (e, colorIndex) => {
    const { name, value } = e.target;
    const colors = [...productData.colors];
    colors[colorIndex][name] = value;
    setProductData((prevState) => ({
      ...prevState,
      colors,
    }));
  };

  const handleSizeChange = (e, colorIndex, sizeIndex) => {
    const { name, value } = e.target;
    const colors = [...productData.colors];
    colors[colorIndex].sizes[sizeIndex][name] = value;
    setProductData((prevState) => ({
      ...prevState,
      colors,
    }));
  };

  const handleAddSize = (colorIndex) => {
    const colors = [...productData.colors];
    colors[colorIndex].sizes.push({ size: "", quantity: 0 });
    setProductData((prevState) => ({
      ...prevState,
      colors,
    }));
  };

  const handleRemoveSize = (colorIndex, sizeIndex) => {
    const colors = [...productData.colors];
    colors[colorIndex].sizes.splice(sizeIndex, 1);
    setProductData((prevState) => ({
      ...prevState,
      colors,
    }));
  };

  const handleAddColor = () => {
    setProductData((prevState) => ({
      ...prevState,
      colors: [...prevState.colors, { name: "", sizes: defaultSizes.map(size => ({ ...size })) }],
    }));
  };

  const handleRemoveColor = (colorIndex) => {
    const colors = [...productData.colors];
    colors.splice(colorIndex, 1);
    setProductData((prevState) => ({
      ...prevState,
      colors,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(createProduct({ data: productData }));
    navigate("/admin/products");
  };

  return (
    <Fragment className="createProductContainer">
      <Typography variant="h3" sx={{ textAlign: "center" }} className="py-10 text-center">
        Add New Product
      </Typography>
      {loading && <p>Loading categories...</p>}
      {error && <p>Error loading categories: {error}</p>}
      <form onSubmit={handleSubmit} className="createProductContainer min-h-screen">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Image URL"
              name="imageUrl"
              value={productData.imageUrl}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Brand"
              name="brand"
              value={productData.brand}
              onChange={handleChange}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Title"
              name="title"
              value={productData.title}
              onChange={handleChange}
            />
          </Grid>

          <Grid item xs={12} spacing={3}>
            {productData.colors.map((color, colorIndex) => (
              <div className="mt-2" key={colorIndex}>
                <Fragment>
                  <Grid container item spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Color Name"
                        name="name"
                        value={color.name}
                        onChange={(event) => handleColorChange(event, colorIndex)}
                        required
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12} sm={2} className="flex items-center">
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() => handleRemoveColor(colorIndex)}
                        disabled={productData.colors.length <= 1}
                      >
                        Remove Color
                      </Button>
                    </Grid>
                  </Grid>

                  {color.sizes.map((size, sizeIndex) => (
                    <Grid container item spacing={5} key={sizeIndex}>
                      <Grid item xs={12} sm={4}>
                        <div className="mt-4">
                          <TextField
                            label="Size"
                            name="size"
                            value={size.size}
                            onChange={(event) => handleSizeChange(event, colorIndex, sizeIndex)}
                            required
                            fullWidth
                          />
                        </div>
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <div className="mt-4">
                          <TextField
                            label="Quantity"
                            name="quantity"
                            type="number"
                            value={size.quantity}
                            onChange={(event) => handleSizeChange(event, colorIndex, sizeIndex)}
                            required
                            fullWidth
                          />
                        </div>
                      </Grid>
                      <Grid item xs={12} sm={2} className="flex items-center">
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => handleRemoveSize(colorIndex, sizeIndex)}
                          disabled={color.sizes.length <= 1}
                        >
                          Remove Size
                        </Button>
                      </Grid>
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <Box mt={2}>
                      <Button variant="outlined" color="primary" onClick={() => handleAddSize(colorIndex)}>
                        Add Size
                      </Button>
                    </Box>
                  </Grid>
                </Fragment>
              </div>
            ))}

            <Grid item xs={12}>
              <Box mt={3}>
                <Button variant="outlined" color="primary" onClick={handleAddColor}>
                  Add Color
                </Button>
              </Box>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select name="category" value={productData.category} onChange={handleChange} label="Category">
                {categories?.categories?.map((cat) => (
                  <MenuItem key={cat._id} value={cat._id}>
                    {cat.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              id="outlined-multiline-static"
              label="Description"
              multiline
              name="description"
              rows={3}
              onChange={handleChange}
              value={productData.description}
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Price"
              name="price"
              value={productData.price}
              onChange={handleChange}
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Discount Percentage"
              name="discountPersent"
              value={productData.discountPersent}
              onChange={handleChange}
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              label="Discounted Price"
              name="discountedPrice"
              value={productData.discountedPrice}
              disabled
              type="number"
            />
          </Grid>

          <Grid item xs={12}>
            <Button variant="contained" sx={{ p: 1.8 }} className="py-20" size="large" type="submit">
              Add New Product
            </Button>
          </Grid>
        </Grid>
      </form>
    </Fragment>
  );
};

export default CreateProductForm;
